import { isBirthdayItself, personFromTitle, normPerson } from "../_shared/birthdayTitle.ts";
import { appendBirthdayContact, type BirthdayTaskClient } from "./birthdayReminderContact.ts";

/**
 * #1670's amendment — when a create_event title IS a birthday, write the
 * thing that RECURS, not just a row for one Friday.
 *
 * The danger the amendment names is not writing a one-off row for a birthday
 * Kin already knows. It is writing one for a birthday Kin does NOT know: a
 * bare `events` row teaches Kin nothing, so next year there is no birthday at
 * all, and the gift heads-up (#1666) and the yearly repeat in the Kin → Apple
 * feed (#1669) never see that person either. She gets one year and silence.
 *
 * So the durable write happens FIRST, and the calendar surfaces derive the
 * rest. #1669's dedupe collapses the derived birthday against a one-off row on
 * the same day for the same person, so writing both is harmless.
 *
 *   a known kid      → family_context  kid   / birthdate
 *   anyone else      → family_context  other / birthdate
 *
 * ⚠️ The amendment's middle row ("anyone else, Pro → a Birthday Reminder
 * contact") is deliberately NOT implemented as a task CREATION. Contacts live
 * inside a `scheduled_tasks` row of type `birthday_reminder`, and chat may
 * never create that task — Family Memory v1.2 §7's ownership guard, which is
 * why `create_scheduled_task`'s enum excludes the type on purpose. Appending
 * to a task she already has would respect the guard, but it needs the config
 * shape and a decision about Pro gating that belongs in its own issue; the
 * amendment's own third row is the correct fallback until then, and it is
 * strictly better than nothing because an "other" birthdate fact already
 * drives #1669's derived calendar birthday.
 */

export interface DurableBirthdayClient {
  from: (table: string) => any;
}

export interface DurableBirthdayResult {
  /** Did the title look like a birthday at all? */
  applicable: boolean;
  /** Did a durable fact actually land (read back, not assumed)? */
  written: boolean;
  /** "kid" or "other" — which category was used. */
  category?: "kid" | "other";
  /** Whose birthday, as stored. */
  person?: string;
  /** Present when the title was a birthday and the write did NOT land. */
  failure?: string;
  /**
   * WHERE it landed — #1721. A kid's birthday is a Family Memory fact; anyone
   * else's is a Birthday Reminder contact on Pro. The reply text differs, so
   * the caller has to be able to tell them apart.
   */
  kind?: "kid_fact" | "birthday_contact";
  /**
   * Applicable, deliberately NOT saved, and NOT an error — a free account
   * saving a non-kid's birthday. Kept separate from `failure` so the reply can
   * be honest without sounding broken.
   */
  skipped?: "free_tier";
}

/** `MM-DD` — a birthdate fact is a day of the year, not a dated event. */
export function birthdateValueFrom(eventDate: string): string {
  return String(eventDate).slice(5, 10);
}

/**
 * Write the recurring birthday fact for `title`, if the title is a birthday.
 *
 * Returns `applicable: false` and touches nothing for every other title, and
 * for "X's birthday PARTY" — a party is a genuine one-off whose date is often
 * a nearby Saturday rather than the day the person was born, so forcing a
 * birthdate out of it records the wrong day forever.
 */
/**
 * The `family_context.source` value for the brain that asked — issue #1686,
 * found by Copilot on #1695.
 *
 * ⚠️ This used to be the literal "chat", which was correct while create_event
 * was chat-only and became wrong the moment voice could call it. Two costs, and
 * the second is the one that matters:
 *
 *   1. A voice-created birthday fact was labelled as having come from chat.
 *   2. `enforce_voice_fact_budget` is scoped to `source = 'voice'`. A voice
 *      write labelled "chat" does not merely go uncounted — it slips past a
 *      control that EXISTS, which is worse than the `events` table having none
 *      (#1694), because nothing would ever report it.
 *
 * Both values are permitted by `family_context_source_check`
 * (20260817200000 added 'voice' for exactly this family of writes).
 */
const FACT_SOURCE_BY_EVENT_SOURCE: Record<string, string> = {
  kin_chat: "chat",
  kin_voice: "voice",
};

export function factSourceFor(eventSource: string | undefined): string {
  // Defaults to "chat" so an unrecognised value cannot write a source the CHECK
  // constraint would reject and lose the birthday fact entirely.
  return FACT_SOURCE_BY_EVENT_SOURCE[String(eventSource ?? "")] ?? "chat";
}

export async function writeDurableBirthday(
  client: DurableBirthdayClient,
  userId: string,
  title: string,
  eventDate: string,
  knownKids: readonly string[],
  // Defaulted, so kin-chat's call site and the #1670 tests are unchanged.
  eventSource: string = "kin_chat",
  // #1721. Defaulted to the pre-#1721 behaviour (a family_context "other"
  // fact) so nothing that has not been updated silently loses a birthday —
  // create_event's executor passes the real values.
  tier: { isPro: boolean } | null = null,
  todayYmd: string = "",
  timeZone: string = "America/New_York",
): Promise<DurableBirthdayResult> {
  if (!isBirthdayItself(title)) return { applicable: false, written: false };

  const person = personFromTitle(title).trim();
  if (!person) return { applicable: false, written: false };

  const wanted = normPerson(person);
  let category: "kid" | "other" = "other";
  let subject = person;
  for (const kid of knownKids) {
    if (normPerson(kid) === wanted) {
      category = "kid";
      subject = kid;
      break;
    }
  }

  const value = birthdateValueFrom(eventDate);

  // ── #1721: a NON-KID birthday no longer becomes a family_context fact ──
  //
  // Pro gets a real Birthday Reminder contact, which is the thing she can see
  // and edit and which actually reminds her. Free saves nothing at all —
  // Kasandra's call on 2026-09-25.
  //
  // `tier === null` means an older caller that has not been updated; it keeps
  // the pre-#1721 behaviour rather than silently dropping her birthday.
  if (category === "other" && tier !== null) {
    if (!tier.isPro) {
      return { applicable: true, written: false, category, person: subject, skipped: "free_tier" };
    }
    const today = todayYmd || new Date().toISOString().slice(0, 10);
    const outcome = await appendBirthdayContact(
      client as unknown as BirthdayTaskClient,
      userId,
      subject,
      value,
      today,
      timeZone,
    );
    if (!outcome.ok) {
      // Read structurally, not by narrowing: tsconfig has strictNullChecks OFF
      // deliberately, and discriminated-union narrowing on a boolean literal
      // does not hold there. Same reason createEventWrite.ts reads `built`
      // this way.
      const reason = (outcome as { reason: string }).reason;
      return { applicable: true, written: false, category, person: subject, failure: reason };
    }
    return { applicable: true, written: true, category, person: subject, kind: "birthday_contact" };
  }

  try {
    // Select-then-update-or-insert, the same shape upsertFamilyFact uses: the
    // partial unique index cannot be a PostgREST on_conflict target.
    const { data: existing, error: selErr } = await client
      .from("family_context")
      .select("id, value")
      .eq("user_id", userId)
      .eq("category", category)
      .eq("subject", subject)
      .eq("key", "birthdate")
      .limit(1);
    if (selErr) {
      console.error("[create_event] birthdate lookup failed:", selErr);
      return { applicable: true, written: false, category, person: subject, failure: "lookup" };
    }

    const row = Array.isArray(existing) ? existing[0] : null;
    if (row?.id) {
      // Already known. Do NOT overwrite: the stored fact may carry a full
      // birthdate including the year (which drives age), and replacing it with
      // this event's MM-DD would throw that away.
      return { applicable: true, written: true, category, person: subject, kind: "kid_fact" };
    }

    // ⚠️ House rule 16 — read the write back. supabase-js resolves on an RLS
    // refusal and a zero-row write returns 200 with error === null.
    const { data: inserted, error: insErr } = await client
      .from("family_context")
      .insert({
        user_id: userId,
        category,
        subject,
        key: "birthdate",
        value,
        source: factSourceFor(eventSource),
        confidence: "high",
        archived: false,
      })
      .select("id")
      .single();
    if (insErr || !inserted?.id) {
      console.error("[create_event] birthdate insert failed or returned no row:", insErr);
      return { applicable: true, written: false, category, person: subject, failure: "insert" };
    }
    return { applicable: true, written: true, category, person: subject, kind: "kid_fact" };
  } catch (e) {
    console.error("[create_event] birthdate write threw:", e);
    return { applicable: true, written: false, category, person: subject, failure: "threw" };
  }
}

/**
 * The sentence appended to create_event's result text when a birthday fact
 * landed. Says the recurring part out loud, because that is the part she
 * cannot see on this year's calendar row.
 */
export function durableBirthdayNote(result: DurableBirthdayResult): string {
  if (!result.applicable) return "";

  // #1721 — free tier, a non-kid birthday. NOT an error, so it must not read
  // like one; but the reply still has to say the birthday itself was not kept,
  // or she reasonably assumes Kin will bring it up next year.
  if (result.skipped === "free_tier") {
    return (
      ` The event is on her calendar, but ${result.person}'s birthday was NOT saved as a recurring one — ` +
      `saved birthdays and their reminders are a Pro feature. Say that plainly and warmly, once, ` +
      `without pitching. Do NOT imply Kin will remember it next year, because Kin will not.`
    );
  }

  if (!result.written) {
    return (
      ` ⚠️ Kin could NOT save ${result.person}'s birthday as a recurring fact, so it will not come back next year. ` +
      `Say that plainly — do not imply Kin will remember it.`
    );
  }

  // Pro, non-kid: it is a Birthday Reminder contact she can see and edit.
  if (result.kind === "birthday_contact") {
    return (
      ` Kin also added ${result.person} to her Birthday Reminders, so it comes back every year ` +
      `and Kin will give her a heads-up a week ahead. Mention that briefly, and that she can change ` +
      `the timing on the Birthday Reminder card in the Tasks tab.`
    );
  }

  return (
    ` Kin also saved this as ${result.person}'s birthday, so it comes back every year on its own ` +
    `and Kin can give her a heads-up about a gift. Mention that briefly.`
  );
}
